import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Services.css";

/* ── images ── */
const heroBg     = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&q=80";
import introImg  from "../assets/images/services/ppt_media_image8.jpg";
const processImg = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&q=80";

/* ── service card images (local) ── */
import svcImg1 from "../assets/images/services/scaffording.jpg";
import svcImg2 from "../assets/images/services/columns.jpg";
import svcImg3 from "../assets/images/services/retaining-wall.jpg";
import svcImg4 from "../assets/images/services/suspended-slab.jpg";
import svcImg5 from "../assets/images/services/ring-beam.jpg";
import svcImg6 from "../assets/images/services/transport.jpg";
import svcImg7 from "../assets/images/services/temporary-work-design.jpg";
import svcImg8 from "../assets/images/services/pr-engineer-services.jpeg";

const svcImages = [svcImg1, svcImg2, svcImg3, svcImg4, svcImg5, svcImg6, svcImg7, svcImg8];

/* ── service SVG icons ── */
import svcIcon1 from "../assets/images/services/services_01.svg";
import svcIcon2 from "../assets/images/services/services_02.svg";
import svcIcon3 from "../assets/images/services/services_03.svg";
import svcIcon4 from "../assets/images/services/services_04.svg";
import svcIcon5 from "../assets/images/services/services_05.svg";
import svcIcon6 from "../assets/images/services/services_06.svg";
import svcIcon7 from "../assets/images/services/services_01.svg";
import svcIcon8 from "../assets/images/services/services_02.svg";

const svcIcons = [svcIcon1, svcIcon2, svcIcon3, svcIcon4, svcIcon5, svcIcon6, svcIcon7, svcIcon8];

/* ── step SVG icons ── */
import stepIcon1 from "../assets/images/services/svg_01.svg";
import stepIcon2 from "../assets/images/services/svg_02.svg";
import stepIcon3 from "../assets/images/services/svg_03.svg";
import stepIcon4 from "../assets/images/services/svg_04.svg";

const stepIcons = [stepIcon1, stepIcon2, stepIcon3, stepIcon4];

/* ── values SVG icons ── */
import iconTrust    from "../assets/images/services/shake-hands.svg";
import iconQuality  from "../assets/images/services/quality-supervision.svg";
import iconHealth   from "../assets/images/services/health.svg";
import iconCrane    from "../assets/images/services/crane.svg";

/* ── disable right-click context menu on the page ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ── animated counter ── */
function Counter({ to, suffix = "", delay = 0 }) {
  const [ref, inView] = useInView();
  const el = useRef(null);
  useEffect(() => {
    if (!inView) return;
    let start = null;
    const run = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 1800, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      if (el.current) el.current.textContent = Math.floor(eased * to);
      if (p < 1) requestAnimationFrame(run);
    };
    setTimeout(() => requestAnimationFrame(run), delay);
  }, [inView, to, delay]);
  return (
    <span ref={ref}>
      <span ref={el}>0</span>{suffix}
    </span>
  );
}

/* ── values data ── */
const values = [
  {
    icon: iconTrust,
    title: "Building With Trust",
    desc: "Our multi-skilled team provides innovative, forward-thinking solutions tailored to each project's unique goals and challenges.",
  },
  {
    icon: iconQuality,
    title: "Experience & Quality",
    desc: "Our team delivers high-quality results through precise execution, strict standards, and a passion for getting it right the first time.",
  },
  {
    icon: iconHealth,
    title: "Health & Safety",
    desc: "We prioritize strict safety standards to protect our people, clients, and communities — ensuring every site is secure, compliant, and risk-aware.",
  },
  {
    icon: iconCrane,
    title: "Commitment To Quality",
    desc: "We uphold high quality standards from materials to delivery — ensuring durability, strong performance, and client satisfaction.",
  },
];

/* ── services data — now with individual page slugs ── */
const services = [
  {
    slug: "framework-and-scaffolding",
    title: "Formwork and Scaffolding",
    desc: "Formwork and scaffolding are temporary construction structures that act as a mold to support wet concrete until it cures, and provide safe working platforms at height.",
  },
  {
    slug: "columns",
    title: "Columns",
    desc: "Columns are vertical structural members primarily designed to support compressive loads, safely transferring forces from beams and slabs to foundations.",
  },
  {
    slug: "retaining-walls",
    title: "Retaining Walls",
    desc: "Retaining walls are engineered structures designed to hold back soil laterally, preventing erosion, slope failure, and protecting adjacent structures.",
  },
  {
    slug: "suspended-slab",
    title: "Suspended Slab",
    desc: "A suspended slab is a reinforced concrete floor or roof that is elevated above the ground and spans between structural supports such as beams, walls, or columns.",
  },
  {
    slug: "ring-beam",
    title: "Ring Beam",
    desc: "A ring beam is a continuous, reinforced concrete beam that runs horizontally along the top of walls, tying them together and distributing loads evenly.",
  },
  {
    slug: "transport",
    title: "Transport",
    desc: "Encompasses the planning, design, and construction of infrastructure networks that facilitate the movement of materials, plant, and personnel to and from site.",
  },
  {
    slug: "temporary-work-design",
    title: "Temporary Work Design",
    desc: "Critical for ensuring structural stability and worker safety, bearing loads such as self-weight of permanent structure during construction until it gains full strength.",
  },
  {
    slug: "pr-engineer-services",
    title: "PR Engineer Services",
    desc: "Encompass the full lifecycle of infrastructure development. These ECSA-registered experts provide certified structural design, sign-off, and project leadership.",
  },
];

/* ── process steps ── */
const steps = [
  {
    n: "STEP 1",
    title: "Initial Consultation & Discovery",
    desc: "We start by understanding your vision, goals, and project requirements — laying the groundwork for a successful build.",
  },
  {
    n: "STEP 2",
    title: "Strategic Planning & Design",
    desc: "Our team creates detailed plans, timelines, and cost estimates, ensuring everything is aligned before construction begins.",
  },
  {
    n: "STEP 3",
    title: "Construction & Execution",
    desc: "With skilled teams and strict supervision, we bring the design to life — on time, on budget, and to the highest standards.",
  },
  {
    n: "STEP 4",
    title: "Final Handover & Follow-Up",
    desc: "We complete final inspections, walk you through the results, and remain available for any post-build support you need.",
  },
];

export default function Services() {
  useNoContextMenu();

  const [heroRef,  heroInView]  = useInView();
  const [introRef, introInView] = useInView();
  const [valRef,   valInView]   = useInView();
  const [svcRef,   svcInView]   = useInView();
  const [procRef,  procInView]  = useInView();

  return (
    <main className="sp">

      {/* ══════ 1. HERO ══════ */}
      <section className="sp-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="sp-hero__overlay" />
        <div className="container sp-hero__body" ref={heroRef}>
          <div className={`sp-hero__label fade-in ${heroInView ? "visible" : ""}`}>
            OUR SERVICES
          </div>
          <h1
            className={`sp-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            WE OFFER THE BEST<br />CONSTRUCTION SOLUTIONS
          </h1>
        </div>
        <div className="sp-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Our Services</span>
        </div>
      </section>

      {/* ══════ 2. INTRO SPLIT + STATS ══════ */}
      <section className="sp-intro" ref={introRef}>

        <div className="container sp-intro__inner">
          {/* left image col */}
          <div className="sp-intro__img-col">
            <img
              src={introImg}
              alt="Bakwena Kit site workers"
              loading="lazy"
              draggable="false"
              onDragStart={(e) => e.preventDefault()}
            />
          </div>

          {/* right text col */}
          <div className={`sp-intro__right fade-in ${introInView ? "visible" : ""}`}>
            <div className="sp-intro__quote">
              <div className="sp-intro__bar" />
              <p>
                Bakwena Kit Formwork and Scaffolding is compliant to the International
                Organisation for Standards ISO9001/2008. Early involvement ensures the best
                results. Our experienced engineering design staff will translate your
                requirements into cost effective formwork &amp; shoring design solutions,
                tailored to the exact needs of the project. By combining our designers'
                knowledge and experience of the capabilities of our equipment range, the
                systems we design are innovative, practical and cost effective.
              </p>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="sp-intro__stats">
          {[
            { to: 85,  suffix: "+", lbl: "EXPERT TEAM MEMBERS" },
            { to: 140, suffix: "",  lbl: "COMPLETED PROJECTS" },
            { to: 99,  suffix: "%", lbl: "HAPPY CLIENTS" },
          ].map((s, i) => (
            <div key={i} className="sp-intro__stat">
              <span className="sp-intro__stat-val">
                <Counter to={s.to} suffix={s.suffix} delay={i * 120} />
              </span>
              <span className="sp-intro__stat-lbl">{s.lbl}</span>
              <span className="sp-intro__stat-line" />
            </div>
          ))}
        </div>

      </section>

      {/* ══════ 3. VALUES — dark 4-col ══════ */}
      <section className="sp-values" ref={valRef}>
        <div className="sp-values__grid container">
          {values.map((v, i) => (
            <div
              key={i}
              className={`sp-val fade-in ${valInView ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <h3 className="sp-val__title">{v.title}</h3>
              <div className="sp-val__icon-wrap">
                <img src={v.icon} alt={v.title} className="sp-val__icon" />
              </div>
              <p className="sp-val__desc">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="sp-values__stripe" aria-hidden="true" />
      </section>

      {/* ══════ 4. SERVICES GRID — image cards ══════ */}
      <section className="sp-svc" ref={svcRef}>
        <div className="container">
          <div className={`sp-svc__head fade-in ${svcInView ? "visible" : ""}`}>
            <div className="sp-svc__tag">OUR SERVICES</div>
            <h2 className="sp-svc__h2">What We Build, We Build Right</h2>
            <p className="sp-svc__sub">
              The best supply &amp; erect, technical solution with a quality product and
              exceptional service at the lowest available price
            </p>
          </div>

          <div className="sp-svc__img-grid">
            {services.map((svc, i) => (
              <div
                key={i}
                className={`sp-svc-img-card fade-in ${svcInView ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.07}s` }}
                onContextMenu={(e) => e.preventDefault()}
              >
                {/* photo */}
                <div className="sp-svc-img-card__photo">
                  <img
                    src={svcImages[i]}
                    alt=""
                    loading="lazy"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                  />
                  <div className="sp-svc-img-card__overlay" />
                  <div className="sp-svc-img-card__shield" aria-hidden="true" />
                </div>

                {/* orange icon box */}
                <div className="sp-svc-img-card__icon-wrap">
                  <img
                    src={svcIcons[i]}
                    alt={svc.title}
                    className="sp-svc-img-card__icon"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                  />
                </div>

                {/* text body */}
                <div className="sp-svc-img-card__body">
                  <h3 className="sp-svc-img-card__title">{svc.title}</h3>
                  <p className="sp-svc-img-card__desc">{svc.desc}</p>
                  {/* ── UPDATED: links to individual service detail page ── */}
                  <Link to={`/services/${svc.slug}`} className="sp-svc-img-card__link">
                    Read More
                    <span className="sp-svc-img-card__arrow">
                      <FiArrowRight size={13} />
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ 5. STEP-BY-STEP PROCESS ══════ */}
      <section className="sp-proc" ref={procRef}>

        {/* left dark panel */}
        <div className="sp-proc__left">
          <div className={`sp-proc__left-inner fade-in ${procInView ? "visible" : ""}`}>
            <h2 className="sp-proc__h2">
              Our <span>Step-by-Step</span>{" "}
              Construction<br />Process
            </h2>
          </div>
          <div className="sp-proc__hazard" aria-hidden="true" />
        </div>

        {/* right image */}
        <div className="sp-proc__img-col">
          <img
            src={processImg}
            alt="Construction crane"
            loading="lazy"
            draggable="false"
            onDragStart={(e) => e.preventDefault()}
          />
        </div>

        {/* full-width white steps card */}
        <div
          className={`sp-proc__card fade-in ${procInView ? "visible" : ""}`}
          style={{ transitionDelay: "0.25s" }}
        >
          {steps.map((step, i) => (
            <div key={i} className="sp-proc__step">
              <div className="sp-proc__step-tag">{step.n}</div>
              <h4 className="sp-proc__step-title">{step.title}</h4>
              <div className="sp-proc__step-icon-row">
                <div className="sp-proc__step-icon-box">
                  <img
                    src={stepIcons[i]}
                    alt={step.title}
                    className="sp-proc__step-icon"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                  />
                </div>
                {i < steps.length - 1 && (
                  <FiArrowRight className="sp-proc__step-arrow" size={18} />
                )}
              </div>
              <p className="sp-proc__step-desc">{step.desc}</p>
            </div>
          ))}
        </div>

      </section>

    </main>
  );
}